import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import axiosInstance from '../../../utils/axios';
import { toast } from 'react-hot-toast';
import { formatINR, formatIndianDate } from '../../../utils/currency';

const QuickActionToolbar = ({ selectedAssets = [], onAction, onRefresh, warrantyData = [] }) => {
  const [loading, setLoading] = useState(false);
  const [showBulkRenewal, setShowBulkRenewal] = useState(false);
  const [showVendorLookup, setShowVendorLookup] = useState(false);
  const [showQuoteRequest, setShowQuoteRequest] = useState(false);
  const [showReplacementOrder, setShowReplacementOrder] = useState(false);
  const [renewalPeriod, setRenewalPeriod] = useState('12months');
  const [searchVendor, setSearchVendor] = useState('');
  const [vendorContacts, setVendorContacts] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [renewalOptions, setRenewalOptions] = useState({
    autoApprove: true,
    extendedWarranty: false,
    notifyOwners: true
  });

  const renewalPeriodOptions = [
    { value: '12months', label: '12 Months (1 Year)' },
    { value: '24months', label: '24 Months (2 Years)' },
    { value: '36months', label: '36 Months (3 Years)' }
  ];

  // Selected asset objects
  const selectedAssetItems = warrantyData.filter(item => selectedAssets.includes(item.id));
  const selectedTotalValue = selectedAssetItems.reduce((s, a) => s + (a.costRaw || 0), 0);

  const quickActions = [
    {
      id: 'bulk-renew',
      label: 'Bulk Renewal',
      icon: 'RefreshCw',
      description: 'Process warranty extensions for selected equipment',
      color: 'text-success bg-success/10',
      disabled: selectedAssets.length === 0,
      action: () => {
        if (selectedAssets.length === 0) {
          toast.error('Please select at least one asset from the grid');
          return;
        }
        setShowBulkRenewal(true);
      }
    },
    {
      id: 'vendor-lookup',
      label: 'Vendor Contacts',
      icon: 'Phone',
      description: 'Directory of suppliers, OEM contacts & SLAs',
      color: 'text-accent bg-accent/10',
      disabled: false,
      action: () => setShowVendorLookup(true)
    },
    {
      id: 'quote-request',
      label: 'Request Quotes',
      icon: 'FileText',
      description: 'Send competitive renewal quote inquiries',
      color: 'text-warning bg-warning/10',
      disabled: selectedAssets.length === 0,
      action: () => {
        if (selectedAssets.length === 0) {
          toast.error('Please select at least one asset from the grid');
          return;
        }
        setShowQuoteRequest(true);
      }
    },
    {
      id: 'replacement-order',
      label: 'Order Replacements',
      icon: 'ShoppingCart',
      description: 'Initiate hardware replacement requisitions',
      color: 'text-error bg-error/10',
      disabled: selectedAssets.length === 0,
      action: () => {
        if (selectedAssets.length === 0) {
          toast.error('Please select at least one asset from the grid');
          return;
        }
        setShowReplacementOrder(true);
      }
    }
  ];

  // Load vendor contacts from live API or warranty data
  const loadVendorContacts = useCallback(async () => {
    try {
      setLoading(true);
      let list = [];
      try {
        const response = await axiosInstance.get('/warranty/vendors');
        if (response.data?.success && Array.isArray(response.data.data)) {
          list = response.data.data;
        }
      } catch (e) {
        console.warn('Fallback to asset vendors:', e);
      }

      // If backend list is empty, build from assets in warrantyData
      if (list.length === 0 && warrantyData.length > 0) {
        const vendorMap = {};
        warrantyData.forEach(asset => {
          const v = asset.vendor || 'OEM Direct';
          if (!vendorMap[v]) {
            vendorMap[v] = {
              id: `v-${v}`,
              name: v,
              contactPerson: 'Vendor Support Executive',
              email: `support@${v.toLowerCase().replace(/[^a-z0-9]/g, '')}.com`,
              phone: '+91 1800 123 4567',
              category: 'Hardware OEM / Supplier',
              total_value: 0,
              asset_count: 0
            };
          }
          vendorMap[v].total_value += (asset.costRaw || 0);
          vendorMap[v].asset_count++;
        });
        list = Object.values(vendorMap);
      }

      setVendorContacts(list);
    } catch (error) {
      console.error('Error loading vendor contacts:', error);
      setVendorContacts([]);
    } finally {
      setLoading(false);
    }
  }, [warrantyData]);

  useEffect(() => {
    loadVendorContacts();
  }, [loadVendorContacts]);

  // Handle bulk renewal
  const handleBulkRenewal = async () => {
    if (selectedAssets.length === 0) {
      toast.error('No assets selected for renewal');
      return;
    }

    try {
      setProcessing(true);
      const response = await axiosInstance.post('/warranty/bulk-renew', {
        asset_ids: selectedAssets,
        period: renewalPeriod
      });

      if (response.data?.success) {
        toast.success(response.data.message || `${selectedAssets.length} warranties renewed successfully!`);
        setShowBulkRenewal(false);
        onRefresh?.();
        onAction?.('renew', { period: renewalPeriod, assets: selectedAssets });
      } else {
        toast.error(response.data?.message || 'Failed to process renewals');
      }
    } catch (error) {
      console.error('Error in bulk renewal:', error);
      toast.error(error.response?.data?.message || 'Failed to process renewals');
    } finally {
      setProcessing(false);
    }
  };

  // Handle quote request
  const handleQuoteRequest = async () => {
    if (selectedAssets.length === 0) {
      toast.error('No assets selected for quotes');
      return;
    }

    setProcessing(true);
    await new Promise(r => setTimeout(r, 600));
    toast.success(`Quote inquiries successfully dispatched for ${selectedAssets.length} assets`);
    setShowQuoteRequest(false);
    setProcessing(false);
  };

  // Handle replacement order
  const handleReplacementOrder = async () => {
    if (selectedAssets.length === 0) {
      toast.error('No assets selected for replacement');
      return;
    }

    setProcessing(true);
    await new Promise(r => setTimeout(r, 600));
    toast.success(`Replacement order requisition created for ${selectedAssets.length} assets (${formatINR(selectedTotalValue, true)})`);
    setShowReplacementOrder(false);
    setProcessing(false);
  };

  const filteredVendors = vendorContacts.filter(vendor => {
    const q = searchVendor.toLowerCase().trim();
    if (!q) return true;
    const name = (vendor.name || vendor.vendor || '').toLowerCase();
    const contact = (vendor.contactPerson || vendor.contact || '').toLowerCase();
    const email = (vendor.email || '').toLowerCase();
    return name.includes(q) || contact.includes(q) || email.includes(q);
  });

  return (
    <div className="space-y-6">
      {/* Quick Actions Grid */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
            <Icon name="Zap" size={20} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Quick Warranty Operations</h3>
            <p className="text-xs text-muted-foreground">
              {selectedAssets.length > 0 
                ? `${selectedAssets.length} asset(s) selected (${formatINR(selectedTotalValue, true)})` 
                : 'Select assets in the grid to execute bulk operations'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={action.action}
              disabled={action.disabled}
              className={`p-5 border border-border rounded-lg transition-smooth text-left group ${
                action.disabled 
                  ? 'opacity-50 cursor-not-allowed bg-muted/10' 
                  : 'hover:bg-muted/40 hover:border-primary/40 cursor-pointer bg-card shadow-subtle'
              }`}
            >
              <div className={`w-11 h-11 rounded-lg flex items-center justify-center mb-3.5 ${action.color} ${
                !action.disabled && 'group-hover:scale-105 transition-transform'
              }`}>
                <Icon name={action.icon} size={22} />
              </div>
              <h4 className="font-semibold text-card-foreground text-sm mb-1">{action.label}</h4>
              <p className="text-xs text-muted-foreground leading-relaxed">{action.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Bulk Renewal Modal */}
      {showBulkRenewal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-card border border-border rounded-xl shadow-elevated w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-card-foreground">Bulk Warranty Renewal</h3>
                <p className="text-xs text-muted-foreground">Extend warranty coverage in database</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowBulkRenewal(false)}
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="p-3.5 bg-success/5 border border-success/20 rounded-lg text-xs">
                <div className="flex items-center justify-between font-semibold text-success mb-1">
                  <span>Target Assets:</span>
                  <span>{selectedAssets.length} Assets Selected</span>
                </div>
                <div className="flex items-center justify-between text-muted-foreground">
                  <span>Total Asset Value:</span>
                  <span className="font-bold text-foreground">{formatINR(selectedTotalValue, false)}</span>
                </div>
              </div>

              <div>
                <Select
                  label="Renewal Extension Period"
                  options={renewalPeriodOptions}
                  value={renewalPeriod}
                  onChange={setRenewalPeriod}
                />
              </div>

              <div className="space-y-2 pt-2">
                <label className="text-xs font-semibold text-card-foreground uppercase tracking-wide">
                  Renewal Policies (INR Standard)
                </label>
                <div className="space-y-2 text-xs">
                  <label className="flex items-center space-x-2 cursor-pointer text-muted-foreground hover:text-foreground">
                    <input 
                      type="checkbox" 
                      className="rounded border-border text-primary focus:ring-primary"
                      checked={renewalOptions.autoApprove}
                      onChange={(e) => setRenewalOptions(prev => ({ ...prev, autoApprove: e.target.checked }))}
                    />
                    <span>Auto-approve renewals under ₹50,000 threshold</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer text-muted-foreground hover:text-foreground">
                    <input 
                      type="checkbox" 
                      className="rounded border-border text-primary focus:ring-primary"
                      checked={renewalOptions.extendedWarranty}
                      onChange={(e) => setRenewalOptions(prev => ({ ...prev, extendedWarranty: e.target.checked }))}
                    />
                    <span>Request OEM comprehensive coverage</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer text-muted-foreground hover:text-foreground">
                    <input 
                      type="checkbox" 
                      className="rounded border-border text-primary focus:ring-primary"
                      checked={renewalOptions.notifyOwners}
                      onChange={(e) => setRenewalOptions(prev => ({ ...prev, notifyOwners: e.target.checked }))}
                    />
                    <span>Notify assigned asset owners upon renewal</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-border flex justify-end space-x-2 bg-muted/10">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBulkRenewal(false)}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleBulkRenewal}
                iconName="RefreshCw"
                iconPosition="left"
                loading={processing}
              >
                Confirm Bulk Renewal
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Vendor Lookup Modal */}
      {showVendorLookup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-card border border-border rounded-xl shadow-elevated w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-95">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-card-foreground">Vendor & OEM Contact Directory</h3>
                <p className="text-xs text-muted-foreground">Active suppliers linked with asset portfolio</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowVendorLookup(false)}
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
            
            <div className="p-5 flex-1 overflow-hidden flex flex-col">
              <Input
                type="search"
                placeholder="Search vendor name, contact person, or email..."
                value={searchVendor}
                onChange={(e) => setSearchVendor(e.target.value)}
                className="mb-4 text-xs"
              />

              <div className="space-y-3 flex-1 overflow-y-auto pr-1">
                {filteredVendors.length > 0 ? (
                  filteredVendors.map((vendor, index) => (
                    <div key={index} className="p-3.5 border border-border rounded-lg hover:border-primary/40 bg-card text-xs">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0 pr-2">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="font-bold text-card-foreground text-sm">
                              {vendor.name || vendor.vendor}
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] bg-primary/10 text-primary font-semibold">
                              {vendor.category || 'Supplier'}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-muted-foreground mt-2">
                            <div>
                              <span>Contact Person:</span> <strong className="text-foreground">{vendor.contactPerson || vendor.contact || 'Account Manager'}</strong>
                            </div>
                            <div>
                              <span>Email:</span> <strong className="text-foreground">{vendor.email || 'N/A'}</strong>
                            </div>
                            <div>
                              <span>Phone:</span> <strong className="text-foreground">{vendor.phone || 'N/A'}</strong>
                            </div>
                            {vendor.total_value > 0 && (
                              <div>
                                <span>Portfolio Value:</span> <strong className="text-success">{formatINR(vendor.total_value, false)}</strong>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-1.5 flex-shrink-0">
                          {vendor.email && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => window.open(`mailto:${vendor.email}`, '_blank')}
                              iconName="Mail"
                            />
                          )}
                          {vendor.phone && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => window.open(`tel:${vendor.phone}`, '_blank')}
                              iconName="Phone"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 text-muted-foreground text-xs">
                    <Icon name="Users" size={36} className="mx-auto mb-2 opacity-60" />
                    No vendors matching search criteria.
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-border flex justify-end bg-muted/10">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowVendorLookup(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Quote Request Modal */}
      {showQuoteRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-card border border-border rounded-xl shadow-elevated w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-card-foreground">Request Renewal Quotes</h3>
                <p className="text-xs text-muted-foreground">{selectedAssets.length} items selected</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowQuoteRequest(false)}
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="p-3.5 bg-warning/5 border border-warning/20 rounded-lg text-xs">
                <p className="text-warning font-semibold mb-1">
                  Generate quote requests for {selectedAssets.length} asset{selectedAssets.length > 1 ? 's' : ''}
                </p>
                <p className="text-muted-foreground">
                  Estimated replacement valuation: <strong className="text-foreground">{formatINR(selectedTotalValue, false)}</strong>
                </p>
              </div>

              <div className="space-y-2 text-xs">
                <label className="font-semibold text-card-foreground uppercase tracking-wide">Inquiry Specifications</label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="checkbox" className="rounded border-border text-primary focus:ring-primary" defaultChecked />
                    <span>Request multi-vendor competitive pricing</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="checkbox" className="rounded border-border text-primary focus:ring-primary" defaultChecked />
                    <span>Include 1-year and 2-year SLA options</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="checkbox" className="rounded border-border text-primary focus:ring-primary" defaultChecked />
                    <span>Include onsite repair turnaround guarantees</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-border flex justify-end space-x-2 bg-muted/10">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowQuoteRequest(false)}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleQuoteRequest}
                iconName="FileText"
                iconPosition="left"
                loading={processing}
              >
                Dispatch Inquiries
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Replacement Order Modal */}
      {showReplacementOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-card border border-border rounded-xl shadow-elevated w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-card-foreground">Hardware Replacement Requisition</h3>
                <p className="text-xs text-muted-foreground">{selectedAssets.length} assets scheduled for renewal</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowReplacementOrder(false)}
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="p-3.5 bg-error/5 border border-error/20 rounded-lg text-xs">
                <p className="text-error font-semibold mb-1">
                  Initiating replacement requisition for {selectedAssets.length} asset{selectedAssets.length > 1 ? 's' : ''}
                </p>
                <p className="text-muted-foreground">
                  Estimated budget required: <strong className="text-foreground">{formatINR(selectedTotalValue, false)}</strong>
                </p>
              </div>

              <div className="space-y-2 text-xs">
                <label className="font-semibold text-card-foreground uppercase tracking-wide">Procurement Terms</label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="checkbox" className="rounded border-border text-primary focus:ring-primary" defaultChecked />
                    <span>Trade-in / Buyback rebate on existing units</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="checkbox" className="rounded border-border text-primary focus:ring-primary" defaultChecked />
                    <span>Include asset tagging & pre-configuration</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="checkbox" className="rounded border-border text-primary focus:ring-primary" defaultChecked />
                    <span>Send requisition to department manager for approval</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-border flex justify-end space-x-2 bg-muted/10">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowReplacementOrder(false)}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleReplacementOrder}
                iconName="ShoppingCart"
                iconPosition="left"
                loading={processing}
              >
                Submit Requisition
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickActionToolbar;
